import React from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';

import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';

import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionIcon from '@mui/icons-material/Description';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import LinkIcon from '@mui/icons-material/Link';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CampaignIcon from '@mui/icons-material/Campaign';

import authenticationService from '../../services/auth.service';
import { createRoute, getParamsFromRoute, isMatchedRoute } from '../../commons/utils/route-helper';
import { Book, CollectionsBookmark } from '@mui/icons-material';
import userService from '../../services/user.service';
import { Box, Button, ListItem, ListItemButton } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import routeService from '../../routes/route.service';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: prop => prop !== 'open' })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

interface RouteWithIcon {
  path: string;
  name?: string;
  icon?: any;
  isSelected?: boolean;
}

const DEFAULT_NAVIGATION_GROUP = [
  {
    name: 'Dashboard',
    path: routeService.getStatisticsPath(),
    icon: <EqualizerIcon />,
  },
  {
    name: 'Users',
    path: routeService.getUsersPath(),
    icon: <PeopleIcon />,
  },
  {
    name: 'Credit package orders',
    path: routeService.getCreditPackageOrderPath(),
    icon: <ShoppingBasketIcon />,
  },
  {
    name: 'System credit',
    path: routeService.getSystemWalletPath(),
    icon: <AccountBalanceWalletIcon />,
  },
  {
    name: 'Pricing settings',
    path: routeService.getCreditPackageManagementPath(),
    icon: <AttachMoneyIcon />,
  },
  {
    name: 'Service tag settings',
    path: routeService.getServiceTagManagementPath(),
    icon: <LocalOfferIcon />,
  },
  {
    name: 'Shortened link',
    path: routeService.getShortenedLinkPath(),
    icon: <LinkIcon />,
  },
  {
    name: 'Import customer',
    path: routeService.getCustomerCsvImportPath(),
    icon: <DescriptionIcon />,
  },
  {
    name: 'Lexoffice',
    path: routeService.getLexofficePath(),
    icon: <img src="/images/lexoffice.png" style={{ width: '24px' }} />,
  },
  {
    name: 'Invoice job',
    path: routeService.getInvoiceJobPath(),
    icon: <ReceiptIcon />,
  },
  {
    name: 'Flyer campaign',
    path: routeService.getFlyerCampaignPath(),
    icon: <CampaignIcon />,
  },
];

const COLLAPSE_ROUTES: RouteWithIcon[] = [
  {
    path: routeService.getUserDetailPath(),
    icon: <PersonIcon />,
  },
  {
    path: routeService.getBusinessObjectDetailPath(),
    icon: <StorefrontIcon />,
  },
  {
    path: routeService.getCategoryDetailPath(),
    icon: <CollectionsBookmark />,
  },
  {
    path: routeService.getProductDetailPath(),
    icon: <Book />,
  },
];

export default function PrivateComponent() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [open, setOpen] = React.useState(false);
  const [navigationGroup, setNavigationGroup] = React.useState<RouteWithIcon[]>([]);

  React.useEffect(() => {
    fetchAndCheckUserAccess();
  }, []);

  const fetchAndCheckUserAccess = async () => {
    const user = await userService.getCurrentLoggedInUser();

    if (!authenticationService.canUserAccessApp(user)) {
      authenticationService.logout();
      navigate(routeService.getLoginPath());
    }
  };

  const redirect = (path: string) => {
    navigate(path);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const logout = () => {
    authenticationService.logout();
    navigate(routeService.getLoginPath());
  };

  React.useEffect(() => {
    const currentUrlPath = window.location.pathname;

    const matchedIndex = COLLAPSE_ROUTES.findIndex(route => isMatchedRoute(route.path, currentUrlPath));

    let extraRoutesToDisplay: RouteWithIcon[] = [];
    let params: any;

    // generate name for all dynamic routes
    if (matchedIndex >= 0) {
      params = getParamsFromRoute(currentUrlPath, COLLAPSE_ROUTES[matchedIndex].path);

      const { userId, boId, categoryId, productId } = params;

      const pathNames = [
        userId ? `User ${userId}` : null,
        boId ? `Business object ${boId}` : null,
        categoryId ? `Category ${categoryId}` : null,
        productId ? `Product ${productId}` : null,
      ];

      extraRoutesToDisplay = COLLAPSE_ROUTES.slice(0, matchedIndex + 1)?.map((r, index) => ({
        ...r,
        name: pathNames[index],
        path: createRoute(r.path, params),
        isSelected: index === matchedIndex,
      }));
    }

    const usersRouteIndex = DEFAULT_NAVIGATION_GROUP.findIndex(r => r.path === routeService.getUsersPath());

    const newNavGroup: RouteWithIcon[] = [
      ...DEFAULT_NAVIGATION_GROUP.slice(0, usersRouteIndex + 1)?.map(r => ({
        ...r,
        isSelected: currentUrlPath === r.path,
      })),
      ...extraRoutesToDisplay,
      ...DEFAULT_NAVIGATION_GROUP.slice(usersRouteIndex + 1)?.map(r => ({
        ...r,
        isSelected: currentUrlPath === r.path,
      })),
    ];

    setNavigationGroup(newNavGroup);
  }, [window.location.pathname]);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            SUPER ADMIN
          </Typography>

          <Button color="inherit" startIcon={<PowerSettingsNewIcon />} onClick={logout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>

        <Divider />

        <List>
          {navigationGroup
            .filter(routeWithIcon => !!routeWithIcon.icon && !!routeWithIcon.name)
            .map(routeWithIcon => (
              <ListItem
                key={routeWithIcon?.name?.toLowerCase().split(' ').join('-')}
                disablePadding
                sx={{ display: 'block' }}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                  }}
                  selected={routeWithIcon.isSelected}
                  onClick={() => {
                    redirect(routeWithIcon.path);
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    {routeWithIcon.icon}
                  </ListItemIcon>
                  <ListItemText primary={routeWithIcon.name} sx={{ opacity: open ? 1 : 0 }} />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <DrawerHeader />
        <Outlet />
      </Box>
    </Box>
  );
}
